import React from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

/**
 * AuthProvider wraps the application with Auth0 authentication context.
 *
 * Configuration:
 * - Uses environment variables for Auth0 domain, client ID, and audience
 * - Enables offline_access for refresh tokens
 * - Uses localStorage for token persistence across page refreshes
 * - Handles post-login redirect via onRedirectCallback
 */
export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  /**
   * Handle redirect after Auth0 authentication completes.
   * This cleans up the URL (removes code/state params) and navigates
   * to the originally requested page (stored in appState.returnTo).
   */
  const onRedirectCallback = (appState) => {
    // Navigate to the return URL or default to dashboard
    navigate(appState?.returnTo || '/', { replace: true });
  };

  // Validate required configuration
  if (!domain || !clientId) {
    console.error('Auth0 configuration missing. Check environment variables.');
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        color: '#ef4444',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <h2>Configuration Error</h2>
        <p>Auth0 environment variables are not configured.</p>
        <p style={{ fontSize: 14, color: '#6b7280', marginTop: 16 }}>
          Please set REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID in your .env file.
        </p>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience,
        scope: 'openid profile email',
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={false}
    >
      {children}
    </Auth0Provider>
  );
}

export default AuthProvider;
